import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { supabase } from '../supabaseClient';

export interface VersionInfo {
  version: string;
  build: number;
}

export interface RemoteVersionInfo {
  platform: string;
  latest_version: string;
  latest_build: number;
  update_url: string;
  is_active: boolean;
  message?: string;
}

// Versión local actual (fallback para Web y base de comparación inicial)
export const LOCAL_VERSION: VersionInfo = {
  version: '1.5.32',
  build: 54
};

/**
 * Obtiene la versión instalada actualmente.
 * En plataformas nativas consulta la información de Capacitor,
 * en Web utiliza el fallback estático local.
 */
export async function getInstalledVersion(): Promise<VersionInfo> {
  if (Capacitor.isNativePlatform()) {
    try {
      const info = await CapacitorApp.getInfo();
      return {
        version: info.version,
        build: parseInt(info.build, 10) || 0
      };
    } catch (e) {
      console.error('Error al obtener la info de la App vía Capacitor:', e);
    }
  }
  return LOCAL_VERSION;
}

/**
 * Compara dos versiones semánticas (ej: "1.5.32" y "1.5.30").
 * Retorna:
 *  1 si v1 > v2
 * -1 si v1 < v2
 *  0 si v1 == v2
 */
export function compareVersions(v1: string, v2: string): number {
  const parts1 = String(v1).split('.').map(Number);
  const parts2 = String(v2).split('.').map(Number);
  
  const maxLength = Math.max(parts1.length, parts2.length);
  for (let i = 0; i < maxLength; i++) {
    const p1 = parts1[i] || 0;
    const p2 = parts2[i] || 0;
    if (p1 > p2) return 1;
    if (p1 < p2) return -1;
  }
  return 0;
}

/**
 * Consulta la versión más reciente disponible en Supabase
 * para la plataforma actual (android, ios, web).
 */
export async function fetchLatestVersion(): Promise<RemoteVersionInfo | null> {
  try {
    const platform = Capacitor.getPlatform(); // 'android' | 'ios' | 'web'
    const { data, error } = await supabase
      .from('app_version_control')
      .select('*')
      .eq('platform', platform)
      .eq('is_active', true)
      .maybeSingle();
      
    if (error) {
      throw error;
    }
    return data as RemoteVersionInfo | null;
  } catch (err) {
    console.warn('Error silencioso al obtener última versión de la BD:', err);
    return null;
  }
}

/**
 * Ejecuta el chequeo completo de versiones y retorna la info
 * si existe una actualización disponible.
 */
export async function checkForUpdate(): Promise<{
  hasUpdate: boolean;
  installed: VersionInfo;
  latest: RemoteVersionInfo;
} | null> {
  try {
    const installed = await getInstalledVersion();
    const latest = await fetchLatestVersion();
    if (!latest) return null;

    const versionComparison = compareVersions(latest.latest_version, installed.version);
    
    let hasUpdate = false;
    if (versionComparison > 0) {
      // Versión disponible es superior
      hasUpdate = true;
    } else if (versionComparison === 0) {
      // Misma versión, comparamos el build/versionCode
      if (latest.latest_build > installed.build) {
        hasUpdate = true;
      }
    }

    if (hasUpdate) {
      return { hasUpdate, installed, latest };
    }
  } catch (e) {
    console.error('Error durante la verificación de actualizaciones:', e);
  }
  return null;
}
